// @ts-check
/**
 * @file push.js
 * @location src/integrations/push.js
 * @role PWA Web Push 클라이언트 — 권한 요청·SW 등록·subscribe·iOS 판정.
 *
 *  서버 동기화는 RPC 래퍼(`api/push.js`)에 위임 — 이 모듈은 브라우저 API 만 만짐.
 *  Service Worker 는 vite-plugin-pwa(injectManifest) 가 빌드 시 등록 — 여기선 ready 만 대기.
 */

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

/** 브라우저가 push 를 지원하나 (Service Worker + PushManager + Notification API). */
export function isPushSupported() {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  );
}

/**
 * iOS 가 PWA 「홈 화면에 추가」 단계 필요한지.
 * iOS Safari 는 standalone PWA 로 설치돼야만 push 수신(16.4+).
 */
export function isIosNeedingInstall(userAgent, isStandalone) {
  const ua = userAgent || '';
  const isIos = /iPhone|iPad|iPod/.test(ua);
  return isIos && !isStandalone;
}

/** base64url → Uint8Array (applicationServerKey 용). */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * 권한 요청 → subscribe → 서버 RPC 등록.
 * 권한 거부/실패 시 null 반환. 성공 시 subscription JSON 반환.
 *
 * @param {{ addPushSubscription: (p: object) => Promise<void> }} rpc
 * @param {string} [publicKey] - VAPID public key (기본 VITE_VAPID_PUBLIC_KEY)
 */
export async function requestAndSubscribe(rpc, publicKey = VAPID_PUBLIC_KEY) {
  if (!isPushSupported()) return null;
  if (!publicKey) {
    console.error('[push] VITE_VAPID_PUBLIC_KEY 미설정');
    return null;
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const j = sub.toJSON();
  await rpc.addPushSubscription({
    endpoint: j.endpoint,
    p256dh: j.keys?.p256dh,
    auth: j.keys?.auth,
    user_agent: navigator.userAgent,
  });
  return sub;
}

/**
 * 현재 푸시 상태 조회 — 설정 토글 UI 용.
 * @returns {Promise<{ supported: boolean, permission: ('granted'|'denied'|'default'|'unsupported'), subscribed: boolean }>}
 */
export async function getPushState() {
  if (!isPushSupported()) return { supported: false, permission: 'unsupported', subscribed: false };
  const permission = Notification.permission;
  let subscribed = false;
  if (permission === 'granted') {
    try {
      const reg = await navigator.serviceWorker.ready;
      subscribed = !!(await reg.pushManager.getSubscription());
    } catch { /* SW 미준비 등 — 미구독 취급 */ }
  }
  return { supported: true, permission, subscribed };
}

/**
 * 현 구독 해제 + 서버 RPC 삭제. 구독 없으면 no-op.
 * @param {{ deletePushSubscription: (p: { endpoint: string }) => Promise<void> }} rpc
 */
export async function unsubscribe(rpc) {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  await rpc.deletePushSubscription({ endpoint });
}
